import type { EventSurveyKind } from '@/features/surveys/api';

export type SurveyEnabledField =
  | 'pre_survey_enabled'
  | 'pre_assignment_enabled'
  | 'post_survey_enabled'
  | 'post_assignment_enabled'
  | 'day_survey_enabled'
  | 'director_survey_enabled';

export interface EventSurveyTabDef {
  kind: EventSurveyKind;
  tabId: string;
  label: string;
  emptyText: string;
  enabledField: SurveyEnabledField;
}

export interface EventSurveyFormOption {
  field: SurveyEnabledField;
  label: string;
}

const ENABLED_FIELD: Record<EventSurveyKind, SurveyEnabledField> = {
  pre: 'pre_survey_enabled',
  assignment: 'pre_assignment_enabled',
  post: 'post_survey_enabled',
  post_assignment: 'post_assignment_enabled',
  day_survey: 'day_survey_enabled',
  director: 'director_survey_enabled',
};

const DEFAULT_TABS: EventSurveyTabDef[] = [
  {
    kind: 'pre',
    tabId: 'surveys-pre',
    label: '事前アンケート',
    emptyText: '事前アンケートは割り当てられていません。',
    enabledField: ENABLED_FIELD.pre,
  },
  {
    kind: 'assignment',
    tabId: 'assignments-pre',
    label: '事前課題',
    emptyText: '事前課題は割り当てられていません。',
    enabledField: ENABLED_FIELD.assignment,
  },
  {
    kind: 'post',
    tabId: 'surveys-post',
    label: '事後アンケート',
    emptyText: '事後アンケートは割り当てられていません。',
    enabledField: ENABLED_FIELD.post,
  },
  {
    kind: 'post_assignment',
    tabId: 'assignments-post',
    label: '事後課題',
    emptyText: '事後課題は割り当てられていません。',
    enabledField: ENABLED_FIELD.post_assignment,
  },
];

/**
 * 月例会（D-329・D-351・D-353）:
 * 事前課題 → 当日アウトプット → 当日アンケート → 事後課題 → 理事講師向け → 社長FB
 */
const MONTHLY_TABS: EventSurveyTabDef[] = [
  {
    kind: 'assignment',
    tabId: 'assignments-pre',
    label: '事前課題',
    emptyText: '事前課題は割り当てられていません。',
    enabledField: ENABLED_FIELD.assignment,
  },
  {
    kind: 'post',
    tabId: 'surveys-post',
    label: '当日アウトプット',
    emptyText: '当日アウトプットは割り当てられていません。',
    enabledField: ENABLED_FIELD.post,
  },
  {
    kind: 'day_survey',
    tabId: 'surveys-day',
    label: '当日アンケート',
    emptyText: '当日アンケートは割り当てられていません。',
    enabledField: ENABLED_FIELD.day_survey,
  },
  {
    kind: 'post_assignment',
    tabId: 'assignments-post',
    label: '事後課題',
    emptyText: '事後課題は割り当てられていません。',
    enabledField: ENABLED_FIELD.post_assignment,
  },
  {
    kind: 'director',
    tabId: 'surveys-director',
    label: '理事講師向けアンケート',
    emptyText: '理事講師向けアンケートは割り当てられていません。',
    enabledField: ENABLED_FIELD.director,
  },
  {
    kind: 'pre',
    tabId: 'surveys-pre',
    label: '社長ミーティングフィードバック',
    emptyText: '社長ミーティングフィードバックは割り当てられていません。',
    enabledField: ENABLED_FIELD.pre,
  },
];

/** 種別ごとの課題・アンケートタブ（表示名・順序・空状態文言） */
export function eventSurveyTabs(typeCode: string | null | undefined): EventSurveyTabDef[] {
  if (typeCode === 'monthly') return MONTHLY_TABS;
  return DEFAULT_TABS.map((tab) =>
    typeCode === 'subcommittee' && tab.kind === 'post_assignment'
      ? { ...tab, label: 'アウトプット', emptyText: 'アウトプットは割り当てられていません。' }
      : tab,
  );
}

/** 種別に応じた区分名（参加イベント結果・ダッシュボード等） */
export function surveyKindLabel(
  typeCode: string | null | undefined,
  kind: EventSurveyKind,
): string {
  return eventSurveyTabs(typeCode).find((tab) => tab.kind === kind)?.label ?? kind;
}

/** イベント詳細のタブ ID（ダッシュボード等の直行リンク用） */
export function surveyTabId(
  typeCode: string | null | undefined,
  kind: EventSurveyKind,
): string {
  return eventSurveyTabs(typeCode).find((tab) => tab.kind === kind)?.tabId ?? 'overview';
}

function surveyKindOrder(typeCode: string | null | undefined, kind: EventSurveyKind): number {
  const idx = eventSurveyTabs(typeCode).findIndex((tab) => tab.kind === kind);
  return idx >= 0 ? idx : 99;
}

/** 種別ごとの表示順に並べ替える */
export function sortSurveysByKindOrder<T extends { survey_kind: string }>(
  typeCode: string | null | undefined,
  surveys: T[],
): T[] {
  return [...surveys].sort(
    (a, b) =>
      surveyKindOrder(typeCode, a.survey_kind as EventSurveyKind) -
      surveyKindOrder(typeCode, b.survey_kind as EventSurveyKind),
  );
}

/** 種別ごとの区分見出し付きでグループ化（空の区分は出さない） */
export function groupSurveysByKind<T extends { survey_kind: string }>(
  typeCode: string | null | undefined,
  surveys: T[],
): { kind: EventSurveyKind; label: string; items: T[] }[] {
  const sorted = sortSurveysByKindOrder(typeCode, surveys);
  return eventSurveyTabs(typeCode)
    .map((tab) => ({
      kind: tab.kind,
      label: tab.label,
      items: sorted.filter((survey) => survey.survey_kind === tab.kind),
    }))
    .filter((group) => group.items.length > 0);
}

/** イベント作成・編集フォームの「使う／使わない」チェックボックス文言 */
export function eventSurveyFormOptions(typeCode: string | null | undefined): EventSurveyFormOption[] {
  if (typeCode === 'monthly') {
    return [
      { field: 'pre_assignment_enabled', label: '事前課題を使う' },
      { field: 'post_survey_enabled', label: '当日アウトプットを使う' },
      { field: 'day_survey_enabled', label: '当日アンケートを使う' },
      { field: 'post_assignment_enabled', label: '事後課題を使う' },
      { field: 'director_survey_enabled', label: '理事講師向けアンケートを使う' },
      { field: 'pre_survey_enabled', label: '社長ミーティングフィードバックを使う' },
    ];
  }
  const postAssignmentLabel = typeCode === 'subcommittee' ? 'アウトプットを使う' : '事後課題を使う';
  return [
    { field: 'pre_survey_enabled', label: '事前アンケートを使う' },
    { field: 'pre_assignment_enabled', label: '事前課題を使う' },
    { field: 'post_survey_enabled', label: '事後アンケートを使う' },
    { field: 'post_assignment_enabled', label: postAssignmentLabel },
  ];
}
